import { ColumnDef } from "@tanstack/react-table";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import SortByIcon from "../../../../../../../assets/svg/sort-by-icon";
import AddIcon from "../../../../../../../assets/svg/add-icon";
import ChevronDownIcon from "../../../../../../../assets/svg/chevron-down-icon";
import HourglassIcon from "../../../../../../../assets/svg/hour-glass-icon";
import { Avatar } from "../../../../../../../components/Avatar/Avatar";
import { getDateTimeByFormat } from "../../../../../../../redux/slices/dateTimeFormatSlice";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { getRelativeDateText } from "../../../../../../../utils/functions/get-relative-date-text";
import { HelpDeskTicket } from "./types";
import { TicketAgent } from "../../TicketDetails/components/TicketOverview/utils/constants";
import {
  AgentAssignmentDropdown,
  type AgentUser,
} from "../../../../AgentAssignmentDropdown";
import { PermissionDisabledWrapper } from "../../PermissionDisabledWrapper";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

const PRIORITY_LABEL_MAP: Record<string, string> = {
  medium: "Moderate",
};

export const getPriorityDisplayLabel = (priorityKey: string) => {
  const lower = priorityKey?.toLowerCase();
  return PRIORITY_LABEL_MAP[lower] ?? capitalize(priorityKey);
};

export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "High":
      return "text-verba-red-alert dark:text-verba-red-alert-dk";
    case "Moderate":
      return "text-dark-yellow-for-alerts dark:text-dark-yellow-for-alerts-dk";
    case "Low":
      return "text-verba-blue dark:text-verba-blue-dk";
    default:
      return "text-data dark:text-data-dk";
  }
};

export const getCategoryStyle = (category: string) => {
  switch (category) {
    case "Help Desk":
    case "Helpdesk Support":
    case "Helpdesk support":
    case "Support":
      return "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk text-verba-indigo dark:text-verba-indigo-dk border-selected-option-highlight dark:border-selected-option-highlight-dk shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]";
    case "Sales & marketing":
    case "Sales & Marketing":
    case "Sales":
      return "bg-tag-yellow-bg dark:bg-tag-yellow-bg-dk text-verba-indigo dark:text-verba-indigo-dk border-tag-yellow-stroke dark:border-tag-yellow-stroke-dk";
    case "Marketing":
      return "bg-tag-lavender-stroke dark:bg-tag-lavender-stroke-dk text-verba-indigo dark:text-verba-indigo-dk border-tag-lavender-stroke dark:border-tag-lavender-stroke-dk";
    default:
      return "bg-tag-yellow-bg dark:bg-tag-yellow-bg-dk text-verba-indigo dark:text-verba-indigo-dk border-tag-yellow-stroke dark:border-tag-yellow-stroke-dk";
  }
};

const capitalize = (str: string) =>
  str ? str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase() : "";

export const getTicketTableColumns = (
  _agents: TicketAgent[] = [],
  agentsList: AgentUser[] = [],
  onAssigneesUpdate?: (ticketId: number, assignees: string[]) => void,
  handleScrollToggle?: (lock: boolean) => void,
  options?: {
    isResolutionEnabled?: boolean;
    stageKey?: string;
    canUpdate?: (ticketCategory?: string) => boolean;
    ticketCategories?: { key: string; display_name: string }[];
    region?: string;
  }
): ColumnDef<HelpDeskTicket>[] => {
  const canUpdate = options?.canUpdate;
  const ticketCategories = options?.ticketCategories ?? [];
  const labels = getHelpdeskLabels(options?.region);

  const getCategoryDisplayName = (categoryKey?: string) => {
    const matchedCategory = ticketCategories?.find(
      (cat) => cat?.key === categoryKey
    );
    return capitalize(matchedCategory?.display_name ?? "");
  };
  const showExternalVendor =
    !!options?.isResolutionEnabled &&
    options?.stageKey === "waiting-on-third-party";

  const externalVendorColumn: ColumnDef<HelpDeskTicket> = {
    accessorKey: "external_vendor",
    size: 200,
    header: () => (
      <div className="text-left px-3 py-2">
        <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
          External Vendor
        </span>
      </div>
    ),
    cell: ({ row }) => {
      const reason = row?.original?.external_vendor;
      if (!reason) return <div className="px-3">-</div>;
      return (
        <div className="px-3">
          <div className="min-h-[22px] flex items-center gap-1 border bg-tags-lavender dark:bg-tags-lavender-dk border-tag-lavender-stroke dark:border-tag-lavender-stroke-dk rounded-md px-2 py-[2px] w-max max-w-full">
            <HourglassIcon className="w-3.5 h-3.5 text-tag-scheduled dark:text-tag-scheduled-dk shrink-0" />
            <TruncatedTextTooltip
              text={reason}
              maxWidth="max-w-[200px]"
              className="text-sm font-medium text-tag-scheduled dark:text-tag-scheduled-dk"
            />
          </div>
        </div>
      );
    },
  };

  const cols: ColumnDef<HelpDeskTicket>[] = [
    {
      accessorKey: "sno",
      size: 60,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            S. No.
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3">
          <span className="text-md text-verba-indigo dark:text-verba-indigo-dk">
            {row?.index + 1}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "ticket_id",
      size: 170,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            {labels.ticketId}
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3 max-w-full overflow-hidden">
          <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap truncate block group-hover/row:text-verba-blue dark:group-hover/row:text-verba-blue-dk group-hover/row:underline group-hover/row:decoration-verba-blue dark:group-hover/row:decoration-verba-blue-dk">
            {labels.ticketPrefix} #{row?.original?.ticket_id}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "ticket_category",
      size: 155,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            {labels.ticketCategory}
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const displayName = getCategoryDisplayName(
          row?.original?.ticket_category
        );
        return (
          <div className="px-3 min-w-[130px] max-w-full overflow-hidden">
            {displayName ? (
              <span
                className={`text-sm font-medium px-2 py-0.5 rounded border ${getCategoryStyle(displayName)}`}
              >
                {displayName}
              </span>
            ) : (
              <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                -
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "title",
      size: 280,
      minSize: 280,
      maxSize: 280,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            {labels.ticketSubject}
          </span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-3 overflow-hidden">
          <TruncatedTextTooltip
            text={row?.original?.title}
            maxWidth="max-w-full"
            className="text-md text-verba-indigo dark:text-verba-indigo-dk group-hover/row:text-verba-blue dark:group-hover/row:text-verba-blue-dk group-hover/row:underline group-hover/row:decoration-verba-blue dark:group-hover/row:decoration-verba-blue-dk"
          />
        </div>
      ),
    },
    {
      accessorKey: "assignees",
      size: 200,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Assignees
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const ticketId = row?.original?.ticket_id;
        const assigneeIds = row?.original?.assignees ?? [];
        const rowCanUpdate =
          canUpdate?.(row?.original?.ticket_category) ?? true;

        const handleApply = async (agentIds: string[], _users: AgentUser[]) => {
          onAssigneesUpdate?.(ticketId, agentIds);
        };

        const resolvedAgents = assigneeIds
          .map((id) =>
            agentsList.find(
              (a) =>
                a?.id === id || (a as AgentUser & { _id?: string })?._id === id
            )
          )
          .filter(Boolean) as AgentUser[];

        const buildTrigger = () => {
          if (resolvedAgents.length === 0) {
            return (
              <div className="flex items-center gap-[2px] mt-1.5 group/assignee">
                <AddIcon className="text-verba-blue dark:text-verba-blue-dk w-[18px] h-[18px]" />
                <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk group-hover/assignee:underline">
                  Add Assignee
                </span>
              </div>
            );
          }

          if (resolvedAgents.length === 1) {
            return (
              <div className="flex items-center gap-1.5">
                <Avatar
                  text={resolvedAgents[0]?.name || ""}
                  maxChars={1}
                  size={22}
                  className="text-xxs"
                />
                <span className="text-md text-verba-indigo dark:text-verba-indigo-dk truncate max-w-[100px]">
                  {resolvedAgents[0]?.name || "-"}
                </span>
                <div className="w-px h-[14px] bg-border-gray dark:bg-border-gray-dk" />
                <ChevronDownIcon className="w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
              </div>
            );
          }

          return (
            <div className="flex items-center gap-1.5">
              <div className="flex items-center -space-x-1.5">
                {resolvedAgents.map((agent) => (
                  <Avatar
                    key={agent?.id}
                    text={agent?.name || ""}
                    maxChars={1}
                    size={22}
                    className="text-xxs ring-1 ring-pure-white dark:ring-pure-white-dk"
                  />
                ))}
              </div>
              <div className="w-px h-[14px] bg-border-gray dark:bg-border-gray-dk" />
              <ChevronDownIcon className="w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
            </div>
          );
        };

        return (
          <div className="px-3" onClick={(e) => e.stopPropagation()}>
            <PermissionDisabledWrapper disabled={!rowCanUpdate}>
              <AgentAssignmentDropdown
                selectedAgentIds={assigneeIds}
                onApply={handleApply}
                agentsList={agentsList}
                subHeading={labels.assignTicketSubheading}
                maxAgents={4}
                hideRole
                canSaveEmpty
                title="ASSIGNEES"
                triggerText="Add Assignee"
                customTrigger={buildTrigger()}
                onToggleScroll={handleScrollToggle}
              />
            </PermissionDisabledWrapper>
          </div>
        );
      },
    },
    {
      accessorKey: "priority",
      size: 105,
      header: () => (
        <div className="flex items-center gap-1 px-3 py-2 cursor-pointer">
          <span className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Priority
          </span>
          <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
        </div>
      ),
      cell: ({ row }) => {
        const priorityLabel = getPriorityDisplayLabel(row?.original?.priority);
        if (!priorityLabel) {
          return <div className="px-3" />;
        }
        const priorityColor = getPriorityColor(priorityLabel);
        return (
          <div className="px-3 flex items-center gap-1">
            <FlaggedIcon className={`w-3.5 h-3.5 shrink-0 ${priorityColor}`} />
            <span
              className={`text-md font-medium whitespace-nowrap ${priorityColor}`}
            >
              {priorityLabel}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "tags",
      size: 210,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Tags
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const tags = row?.original?.tags ?? [];
        const visibleTags = tags?.slice(0, 2);
        const extraCount = Math.max(0, tags?.length - 2);
        if (tags?.length === 0) {
          return (
            <div className="px-3 min-w-[130px] max-w-full overflow-hidden">
              <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                -
              </span>
            </div>
          );
        }
        return (
          <div className="px-3 min-w-[130px] flex items-center gap-1.5 max-w-full overflow-hidden">
            {visibleTags?.map((tag, i) => (
              <span
                key={i}
                className="text-sm font-normal text-data dark:text-data-dk bg-tag-gray dark:bg-tag-gray-dk px-[6px] py-[2px] rounded whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
            {extraCount > 0 && (
              <span className="text-sm font-normal text-dark-gray dark:text-dark-gray-dk whitespace-nowrap">
                +{extraCount}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_by",
      size: 155,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Created By
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const createdBy = row?.original?.created_by;
        const name =
          (typeof createdBy === "object" ? createdBy?.name : createdBy) || "-";
        return (
          <div className="px-3 flex items-center gap-1.5 max-w-full overflow-hidden">
            <Avatar text={name} size={22} className="text-xxs" />
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      size: 165,
      header: () => (
        <div className="flex items-center gap-1 px-3 py-2 cursor-pointer">
          <span className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Last Activity
          </span>
          <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
        </div>
      ),
      cell: ({ row }) => {
        const relative = getRelativeDateText(row?.original?.updated_at);
        const time = getDateTimeByFormat(
          new Date(row?.original?.updated_at)
        )?.time;
        return (
          <div className="px-3 max-w-full overflow-hidden">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {relative} at {time}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      size: 185,
      header: () => (
        <div className="flex items-center gap-1 px-3 py-2 cursor-pointer">
          <span className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Created
          </span>
          <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
        </div>
      ),
      cell: ({ row }) => {
        const dt = getDateTimeByFormat(new Date(row?.original?.created_at));
        return (
          <div className="px-3 max-w-full overflow-hidden">
            <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {dt?.date}, {dt?.time}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "property_name",
      size: 130,
      header: () => (
        <div className="text-left px-3 py-2">
          <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
            Property Name
          </span>
        </div>
      ),
      cell: ({ row }) => {
        const propertyName = row?.original?.property_name;
        return (
          <div className="px-3 min-w-[130px] max-w-full overflow-hidden">
            {propertyName ? (
              <span className="text-md text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                {propertyName}
              </span>
            ) : (
              <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                -
              </span>
            )}
          </div>
        );
      },
    },
  ];

  if (showExternalVendor) {
    const priorityIdx = cols.findIndex(
      (c) => (c as { accessorKey?: string }).accessorKey === "priority"
    );
    cols.splice(priorityIdx + 1, 0, externalVendorColumn);
  }

  return cols;
};
